import { GET } from 'common/helpers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import type { SupportRequest } from 'types/SupportRequest.ts';
import { useCountdown } from '#admin/hook/useCountdown.ts';
import { useMeSuspense } from '#admin/hook/useMe.tsx';

const BannerContent: React.FC<{ activeRequest: SupportRequest; onExpired: () => void }> = ({ activeRequest, onExpired }) => {
  const { t } = useTranslation();
  const { time, isExpired } = useCountdown(new Date(activeRequest.expires_at ?? ''));

  React.useEffect(() => {
    if (!isExpired) return;
    const id = setTimeout(onExpired, 10_000);
    return () => clearTimeout(id);
  }, [isExpired, onExpired]);

  // Bare tokens rather than the numbered aliases: both states pair a fill with text on top of it,
  // and mapping the numbered steps by hue would put dark-on-dark (1.38:1 and 2.59:1).
  return (
    <div className={`fixed top-[65px] left-0 z-30 w-full px-4 py-2 text-center font-semibold text-sm ${isExpired ? 'bg-red text-white' : 'bg-yellow text-grey-dark'}`}>
      {isExpired ? t('supportAccess.bannerExpired') : t('supportAccess.banner', { role: activeRequest.requested_role_name, time })}
    </div>
  );
};

export const SupportModeBanner: React.FC = () => {
  const { mutate: mutateMe } = useMeSuspense();
  const { data: activeRequest, mutate } = useSWR<SupportRequest | null>(['/support-request/my-active'], GET, {
    refreshInterval: 10_000,
  });

  // The nav and effective permissions come from /auth/me, which is fetched once. When this poll detects the
  // support-access state changing (granted / revoked / expired), refetch /auth/me so the operator's tabs and
  // permissions update live — otherwise they'd persist (incl. elevated access after a revoke) until a manual refresh.
  const prevKey = React.useRef<string | null | undefined>(undefined);
  React.useEffect(() => {
    const key = activeRequest ? `${activeRequest.id}:${activeRequest.status}` : null;
    if (prevKey.current !== undefined && prevKey.current !== key) mutateMe();
    prevKey.current = key;
  }, [activeRequest, mutateMe]);

  if (!activeRequest) return null;

  return <BannerContent activeRequest={activeRequest} onExpired={() => void mutate()} />;
};
